import React, { useState, useEffect } from "react";
import { useSitemap } from "../utils/sitemapGenerator";
import { useRSSGenerator } from "../utils/rssGenerator";
import { useStructuredData } from "../hooks/useStructuredData";
import { useFetch } from "../hooks/useFetch";
import { apiEndpoints } from "../utils/api";

export const SEODashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [seoAnalysis, setSeoAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [validationResults, setValidationResults] = useState({});

  const { generateAndDownloadSitemap, generateAndDownloadRobots } = useSitemap();
  const { downloadRSSFeed } = useRSSGenerator();
  const { generateOrganizationSchema, generateWebSiteSchema } = useStructuredData();

  // Cargar datos para análisis
  const { data: settings } = useFetch(apiEndpoints.settings.public());
  const { data: postsResponse } = useFetch(apiEndpoints.posts.list(1, 100));

  const posts = postsResponse?.data || [];

  // Análisis completo de SEO mejorado
  const runCompleteSEOAnalysis = async () => {
    setIsAnalyzing(true);

    const analysis = {
      timestamp: new Date().toISOString(),
      overallScore: 0,
      categories: {
        technical: { score: 0, items: [], issues: [] },
        content: { score: 0, items: [], issues: [] },
        social: { score: 0, items: [], issues: [] },
        images: { score: 0, items: [], issues: [] },
        performance: { score: 0, items: [], issues: [] },
      },
      recommendations: [],
      criticalIssues: [],
      goodPractices: [],
    };

    // Análisis técnico mejorado
    const technicalChecks = [
      {
        name: "Título del sitio configurado",
        check: () => !!settings?.site?.name,
        weight: 20,
        issue: "Configure el título principal del sitio",
      },
      {
        name: "Meta descripción del sitio",
        check: () => !!settings?.site?.description && settings.site.description.length >= 50,
        weight: 15,
        issue: "Configure una descripción del sitio de al menos 50 caracteres",
      },
      {
        name: "Logo del sitio",
        check: () => !!settings?.site?.logo,
        weight: 10,
        issue: "Configure un logo del sitio para mejorar la imagen de marca",
      },
      {
        name: "Información de contacto",
        check: () => !!(settings?.contact?.email || settings?.contact?.phone),
        weight: 15,
        issue: "Configure información de contacto completa",
      },
      {
        name: "URL canónica configurada",
        check: () => !!settings?.site?.url,
        weight: 20,
        issue: "Configure la URL canónica del sitio",
      },
      {
        name: "Sitemap.xml disponible",
        check: () => true, // Asumimos que está disponible si se puede generar
        weight: 20,
        issue: "Genere y mantenga actualizado el sitemap.xml",
      },
    ];

    let technicalScore = 0;
    technicalChecks.forEach((check) => {
      const passed = check.check();
      analysis.categories.technical.items.push({
        name: check.name,
        status: passed ? "pass" : "fail",
        weight: check.weight,
        message: passed ? "✅ Configurado correctamente" : `❌ ${check.issue}`,
      });
      
      if (passed) {
        technicalScore += check.weight;
        analysis.goodPractices.push(check.name);
      } else {
        analysis.categories.technical.issues.push(check.issue);
        if (check.weight >= 20) {
          analysis.criticalIssues.push(check.issue);
        }
      }
    });
    
    analysis.categories.technical.score = Math.round(
      (technicalScore / technicalChecks.reduce((acc, c) => acc + c.weight, 0)) * 100
    );

    // Análisis de contenido mejorado
    const postsWithDescription = posts.filter(p => {
      const desc = p.description || p.excerpt || p.summary;
      return desc && desc.length >= 100;
    }).length;

    const postsWithImages = posts.filter(p => 
      !!(p.featured_image || p.image)
    ).length;

    const postsWithTags = posts.filter(p => {
      if (typeof p.tags === 'string') return p.tags.split(',').length >= 3;
      if (Array.isArray(p.tags)) return p.tags.length >= 3;
      return false;
    }).length;

    const postsWithGoodTitles = posts.filter(p => {
      const title = p.title || '';
      return title.length >= 30 && title.length <= 60;
    }).length;

    const contentChecks = [
      {
        name: "Posts con meta descripción (100+ caracteres)",
        value: postsWithDescription,
        total: posts.length,
        threshold: 0.8,
        issue: "Mejore las meta descripciones de los posts"
      },
      {
        name: "Posts con imágenes destacadas",
        value: postsWithImages,
        total: posts.length,
        threshold: 0.9,
        issue: "Agregue imágenes destacadas a más posts"
      },
      {
        name: "Posts con etiquetas (3+)",
        value: postsWithTags,
        total: posts.length,
        threshold: 0.7,
        issue: "Agregue más etiquetas descriptivas a los posts"
      },
      {
        name: "Títulos SEO optimizados (30-60 caracteres)",
        value: postsWithGoodTitles,
        total: posts.length,
        threshold: 0.8,
        issue: "Optimice la longitud de títulos para SEO"
      }
    ];

    let contentScore = 0;
    contentChecks.forEach((check) => {
      const percentage = check.total > 0 ? check.value / check.total : 0;
      const passed = percentage >= check.threshold;
      const status = passed ? "pass" : percentage > check.threshold * 0.5 ? "warning" : "fail";
      
      analysis.categories.content.items.push({
        name: check.name,
        status: status,
        value: check.value,
        total: check.total,
        percentage: Math.round(percentage * 100),
        message: `${check.value}/${check.total} posts (${Math.round(percentage * 100)}%)`
      });
      
      contentScore += percentage * 25;
      
      if (!passed) {
        analysis.categories.content.issues.push(check.issue);
      }
    });
    
    analysis.categories.content.score = Math.round(contentScore);

    // Análisis de imágenes
    const imagesWithAlt = posts.filter(p => !!(p.image_alt || p.alt_text)).length;
    const imagesOptimized = posts.filter(p => {
      const img = p.featured_image || p.image;
      return img && !img.includes('?') && (img.includes('.webp') || img.includes('.jpg') || img.includes('.png'));
    }).length;

    analysis.categories.images.items = [
      {
        name: "Imágenes con texto alternativo",
        value: imagesWithAlt,
        total: postsWithImages,
        percentage: postsWithImages > 0 ? Math.round((imagesWithAlt / postsWithImages) * 100) : 0,
        status: imagesWithAlt / postsWithImages > 0.8 ? "pass" : "fail"
      },
      {
        name: "Imágenes optimizadas",
        value: imagesOptimized,
        total: postsWithImages,
        percentage: postsWithImages > 0 ? Math.round((imagesOptimized / postsWithImages) * 100) : 0,
        status: imagesOptimized / postsWithImages > 0.7 ? "pass" : "warning"
      }
    ];

    analysis.categories.images.score = Math.round(
      ((imagesWithAlt / Math.max(postsWithImages, 1)) * 50) + 
      ((imagesOptimized / Math.max(postsWithImages, 1)) * 50)
    );

    // Análisis de redes sociales mejorado
    const socialChecks = [
      {
        name: "Facebook configurado",
        check: () => !!settings?.social?.facebook,
        weight: 25,
      },
      {
        name: "Twitter/X configurado",
        check: () => !!settings?.social?.twitter,
        weight: 25,
      },
      {
        name: "Instagram configurado",
        check: () => !!settings?.social?.instagram,
        weight: 20,
      },
      {
        name: "YouTube configurado",
        check: () => !!settings?.social?.youtube,
        weight: 15,
      },
      {
        name: "LinkedIn configurado",
        check: () => !!settings?.social?.linkedin,
        weight: 15,
      },
    ];

    let socialScore = 0;
    socialChecks.forEach((check) => {
      const passed = check.check();
      analysis.categories.social.items.push({
        name: check.name,
        status: passed ? "pass" : "fail",
        weight: check.weight,
      });
      if (passed) socialScore += check.weight;
    });
    
    analysis.categories.social.score = Math.round(
      (socialScore / socialChecks.reduce((acc, c) => acc + c.weight, 0)) * 100
    );

    // Análisis de rendimiento estimado
    analysis.categories.performance.items = [
      {
        name: "Tiempo de carga estimado",
        status: "warning",
        message: "Ejecute una auditoría de velocidad para obtener datos precisos"
      },
      {
        name: "Optimización móvil",
        status: "pass",
        message: "Diseño responsive implementado"
      },
      {
        name: "HTTPS habilitado",
        status: window.location.protocol === 'https:' ? "pass" : "fail",
        message: window.location.protocol === 'https:' ? "Sitio seguro con HTTPS" : "Implementar certificado SSL"
      }
    ];
    
    analysis.categories.performance.score = 75; // Estimación

    // Puntuación general
    analysis.overallScore = Math.round(
      analysis.categories.technical.score * 0.25 +
      analysis.categories.content.score * 0.30 +
      analysis.categories.social.score * 0.15 +
      analysis.categories.images.score * 0.15 +
      analysis.categories.performance.score * 0.15
    );

    // Generar recomendaciones inteligentes
    if (analysis.categories.technical.score < 80) {
      analysis.recommendations.push("Completar configuración técnica básica del sitio");
    }
    if (analysis.categories.content.score < 70) {
      analysis.recommendations.push("Mejorar optimización de contenido (títulos, descripciones, etiquetas)");
    }
    if (analysis.categories.social.score < 60) {
      analysis.recommendations.push("Configurar más perfiles de redes sociales");
    }
    if (analysis.categories.images.score < 70) {
      analysis.recommendations.push("Optimizar imágenes y agregar texto alternativo");
    }

    // Recomendaciones adicionales basadas en el score general
    if (analysis.overallScore >= 90) {
      analysis.recommendations.push("¡Excelente! Mantener las buenas prácticas actuales");
    } else if (analysis.overallScore >= 80) {
      analysis.recommendations.push("Buen trabajo. Enfocarse en los elementos con puntuación más baja");
    } else if (analysis.overallScore >= 60) {
      analysis.recommendations.push("Hay margen de mejora. Priorizar los issues críticos");
    } else {
      analysis.recommendations.push("Se requiere trabajo significativo en SEO. Comenzar con aspectos técnicos");
    }

    setSeoAnalysis(analysis);
    setIsAnalyzing(false);
  };

  // Validar URLs de herramientas externas
  const validateExternalTools = async () => {
    const tools = [
      { name: "Google Search Console", url: "https://search.google.com/search-console" },
      { name: "Rich Results Test", url: "https://search.google.com/test/rich-results" },
      { name: "Facebook Debugger", url: "https://developers.facebook.com/tools/debug/" }
    ];

    const results = {};
    for (const tool of tools) {
      try {
        const response = await fetch(tool.url, { method: 'HEAD', mode: 'no-cors' });
        results[tool.name] = { status: 'available', url: tool.url };
      } catch (error) {
        results[tool.name] = { status: 'unknown', url: tool.url };
      }
    }
    setValidationResults(results);
  };

  useEffect(() => {
    if (settings && posts.length > 0) {
      runCompleteSEOAnalysis();
    }
  }, [settings, posts.length]);

  useEffect(() => {
    validateExternalTools();
  }, []);

  const renderOverview = () => (
    <div className="overview-grid">
      <div className="score-card">
        <div
          className={`score-circle ${
            seoAnalysis?.overallScore >= 90
              ? "excellent"
              : seoAnalysis?.overallScore >= 70
              ? "good"
              : seoAnalysis?.overallScore >= 50
              ? "warning"
              : "poor"
          }`}
        >
          {seoAnalysis?.overallScore || 0}%
        </div>
        <h3>Puntuación SEO General</h3>
        <p>
          {seoAnalysis?.overallScore >= 90
            ? "¡Excelente optimización!"
            : seoAnalysis?.overallScore >= 70
            ? "Buena optimización"
            : seoAnalysis?.overallScore >= 50
            ? "Optimización aceptable"
            : "Requiere mejoras"}
        </p>
      </div>

      <div className="quick-stats">
        <div className="stat-item">
          <span className="stat-number">{posts.length}</span>
          <span className="stat-label">Posts Totales</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {posts.filter((p) => p.description || p.excerpt).length}
          </span>
          <span className="stat-label">Con Meta Descripción</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {posts.filter((p) => p.featured_image || p.image).length}
          </span>
          <span className="stat-label">Con Imagen Destacada</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {seoAnalysis?.criticalIssues?.length || 0}
          </span>
          <span className="stat-label">Issues Críticos</span>
        </div>
      </div>

      <div className="category-scores">
        {seoAnalysis && Object.entries(seoAnalysis.categories).map(([category, data]) => (
          <div key={category} className="category-score-item">
            <div className="category-info">
              <span className="category-name">
                {category === 'technical' ? 'Técnico' :
                 category === 'content' ? 'Contenido' :
                 category === 'social' ? 'Social' :
                 category === 'images' ? 'Imágenes' :
                 'Rendimiento'}
              </span>
              <span className={`category-score ${
                data.score >= 80 ? 'excellent' : 
                data.score >= 60 ? 'good' : 
                data.score >= 40 ? 'warning' : 'poor'
              }`}>
                {data.score}%
              </span>
            </div>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ width: `${data.score}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {seoAnalysis?.recommendations &&
        seoAnalysis.recommendations.length > 0 && (
          <div className="recommendations-card">
            <h3>🎯 Recomendaciones Principales</h3>
            <ul>
              {seoAnalysis.recommendations.slice(0, 5).map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        )}

      {seoAnalysis?.criticalIssues && seoAnalysis.criticalIssues.length > 0 && (
        <div className="critical-issues-card">
          <h3>⚠️ Issues Críticos</h3>
          <ul>
            {seoAnalysis.criticalIssues.map((issue, index) => (
              <li key={index}>{issue}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const renderAnalysis = () => (
    <div className="analysis-section">
      <div className="analysis-header">
        <h3>🔍 Análisis Detallado de SEO</h3>
        <button
          className="refresh-button"
          onClick={runCompleteSEOAnalysis}
          disabled={isAnalyzing}
        >
          {isAnalyzing ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Analizando...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt"></i>
              Actualizar Análisis
            </>
          )}
        </button>
      </div>

      {seoAnalysis && (
        <div className="analysis-grid">
          {Object.entries(seoAnalysis.categories).map(([category, data]) => (
            <div key={category} className="category-card">
              <div className="category-header">
                <h4>
                  {category === 'technical' ? '🔧 Aspectos Técnicos' :
                   category === 'content' ? '📝 Contenido' :
                   category === 'social' ? '📱 Redes Sociales' :
                   category === 'images' ? '🖼️ Imágenes' :
                   '⚡ Rendimiento'}
                </h4>
                <span
                  className={`score-badge ${
                    data.score >= 80
                      ? "excellent"
                      : data.score >= 60
                      ? "good" 
                      : data.score >= 40
                      ? "warning"
                      : "poor"
                  }`}
                >
                  {data.score}%
                </span>
              </div>
              <div className="category-items">
                {data.items.map((item, index) => (
                  <div key={index} className="analysis-item">
                    <div className="item-header">
                      <span className="item-name">{item.name}</span>
                      <span className={`item-status ${item.status}`}>
                        {item.status === "pass"
                          ? "✅"
                          : item.status === "warning"
                          ? "⚠️"
                          : "❌"}
                        {item.percentage !== undefined && ` (${item.percentage}%)`}
                      </span>
                    </div>
                    {item.message && (
                      <div className="item-message">{item.message}</div>
                    )}
                  </div>
                ))}
              </div>
              {data.issues && data.issues.length > 0 && (
                <div className="category-issues">
                  <h5>Mejoras sugeridas:</h5>
                  <ul>
                    {data.issues.map((issue, idx) => (
                      <li key={idx}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderTools = () => (
    <div className="tools-grid">
      <div className="tool-card">
        <div className="tool-header">
          <h3>Archivos SEO</h3>
          <i className="fas fa-file-code"></i>
        </div>
        <div className="tool-buttons">
          <button className="tool-button primary" onClick={generateAndDownloadSitemap}>
            <i className="fas fa-sitemap"></i>
            Descargar Sitemap.xml
          </button>
          <button className="tool-button primary" onClick={generateAndDownloadRobots}>
            <i className="fas fa-robot"></i>
            Descargar Robots.txt
          </button>
          <button className="tool-button secondary" onClick={downloadRSSFeed}>
            <i className="fas fa-rss"></i>
            Descargar RSS Feed
          </button>
        </div>
        <p>Genera archivos esenciales para motores de búsqueda</p>
      </div>

      <div className="tool-card">
        <div className="tool-header">
          <h3>Datos Estructurados</h3>
          <i className="fas fa-code"></i>
        </div>
        <div className="tool-buttons">
          <button
            className="tool-button secondary"
            onClick={() => {
              const schema = generateOrganizationSchema();
              const blob = new Blob([JSON.stringify(schema, null, 2)], {
                type: 'application/json'
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'organization-schema.json';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <i className="fas fa-building"></i>
            Descargar Schema Organization
          </button>
          <button
            className="tool-button secondary"
            onClick={() => {
              const schema = generateWebSiteSchema();
              const blob = new Blob([JSON.stringify(schema, null, 2)], {
                type: 'application/json'
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'website-schema.json';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <i className="fas fa-globe"></i>
            Descargar Schema WebSite
          </button>
        </div>
        <p>Descarga los esquemas de datos estructurados</p>
      </div>

      <div className="tool-card">
        <div className="tool-header">
          <h3>Herramientas de Validación</h3>
          <i className="fas fa-check-circle"></i>
        </div>
        <div className="validation-links">
          <a
            href="https://search.google.com/test/rich-results"
            target="_blank"
            rel="noopener noreferrer"
            className="validation-link"
          >
            <i className="fab fa-google"></i>
            Test de Resultados Enriquecidos
          </a>
          <a
            href="https://developers.facebook.com/tools/debug/"
            target="_blank"
            rel="noopener noreferrer"
            className="validation-link"
          >
            <i className="fab fa-facebook"></i>
            Facebook Sharing Debugger
          </a>
          <a
            href="https://cards-dev.twitter.com/validator"
            target="_blank"
            rel="noopener noreferrer"
            className="validation-link"
          >
            <i className="fab fa-twitter"></i>
            Twitter Card Validator
          </a>
          <a
            href="https://search.google.com/search-console"
            target="_blank"
            rel="noopener noreferrer"
            className="validation-link"
          >
            <i className="fas fa-search"></i>
            Google Search Console
          </a>
          <a
            href="https://www.linkedin.com/post-inspector/"
            target="_blank"
            rel="noopener noreferrer"
            className="validation-link"
          >
            <i className="fab fa-linkedin"></i>
            LinkedIn Post Inspector
          </a>
        </div>
        <p>Valida el SEO en herramientas externas</p>
      </div>

      <div className="tool-card">
        <div className="tool-header">
          <h3>Análisis de Competencia</h3>
          <i className="fas fa-chart-line"></i>
        </div>
        <div className="competitor-tools">
          <a
            href="https://trends.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="validation-link"
          >
            <i className="fas fa-trending-up"></i>
            Google Trends
          </a>
          <a
            href="https://answerthepublic.com"
            target="_blank"
            rel="noopener noreferrer"
            className="validation-link"
          >
            <i className="fas fa-question-circle"></i>
            Answer The Public
          </a>
        </div>
        <p>Herramientas para análisis competitivo y palabras clave</p>
      </div>
    </div>
  );

  return (
    <div className="seo-dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard SEO Avanzado</h1>
        <p className="dashboard-description">
          Gestión integral del SEO de Red Arauca
        </p>
        {seoAnalysis && (
          <div className="dashboard-stats">
            <div className="dashboard-stat">
              <i className="fas fa-trophy"></i>
              <span>Score: {seoAnalysis.overallScore}%</span>
            </div>
            <div className="dashboard-stat">
              <i className="fas fa-exclamation-triangle"></i>
              <span>Issues: {seoAnalysis.criticalIssues?.length || 0}</span>
            </div>
            <div className="dashboard-stat">
              <i className="fas fa-clock"></i>
              <span>Última actualización: {new Date(seoAnalysis.timestamp).toLocaleDateString()}</span>
            </div>
          </div>
        )}
      </div>

      <nav className="tab-navigation">
        <button
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <i className="fas fa-dashboard"></i>
          Resumen
        </button>
        <button
          className={`tab-button ${activeTab === "analysis" ? "active" : ""}`}
          onClick={() => setActiveTab("analysis")}
        >
          <i className="fas fa-chart-pie"></i>
          Análisis Detallado
        </button>
        <button
          className={`tab-button ${activeTab === "tools" ? "active" : ""}`}
          onClick={() => setActiveTab("tools")}
        >
          <i className="fas fa-tools"></i>
          Herramientas
        </button>
      </nav>

      <div className="tab-content">
        {activeTab === "overview" && renderOverview()}
        {activeTab === "analysis" && renderAnalysis()}
        {activeTab === "tools" && renderTools()}
      </div>

      <style jsx>{`
        .seo-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          background: #f8fafc;
          min-height: 100vh;
        }

        .dashboard-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 40px;
          border-radius: 16px;
          margin-bottom: 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .dashboard-title {
          font-size: 2.5rem;
          font-weight: bold;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
        }

        .dashboard-description {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 20px;
        }

        .dashboard-stats {
          display: flex;
          gap: 30px;
          flex-wrap: wrap;
          margin-top: 20px;
        }

        .dashboard-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.2);
          padding: 12px 16px;
          border-radius: 25px;
          backdrop-filter: blur(10px);
          font-weight: 500;
        }

        .tab-navigation {
          display: flex;
          gap: 4px;
          background: white;
          padding: 6px;
          border-radius: 12px;
          margin-bottom: 30px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .tab-button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px 24px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
          color: #6b7280;
        }

        .tab-button.active {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .tab-content {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .overview-grid {
          display: grid;
          grid-template-columns: 300px 1fr 350px;
          gap: 30px;
          padding: 40px;
        }

        .score-card {
          text-align: center;
          background: linear-gradient(135deg, #f8fafc, #e2e8f0);
          padding: 30px;
          border-radius: 16px;
        }

        .score-circle {
          width: 140px;
          height: 140px;
          border-radius: 50%;
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.2rem;
          font-weight: bold;
          color: white;
          position: relative;
          background: conic-gradient(
            from 0deg,
            var(--score-color) 0%,
            var(--score-color) calc(var(--score) * 3.6deg),
            #e5e7eb calc(var(--score) * 3.6deg),
            #e5e7eb 360deg
          );
        }

        .score-circle::before {
          content: '';
          position: absolute;
          width: 100px;
          height: 100px;
          background: white;
          border-radius: 50%;
          z-index: 1;
        }

        .score-circle.excellent {
          --score-color: #10b981;
        }
        .score-circle.good {
          --score-color: #3b82f6;
        }
        .score-circle.warning {
          --score-color: #f59e0b;
        }
        .score-circle.poor {
          --score-color: #ef4444;
        }

        .quick-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .stat-item {
          text-align: center;
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc, #e2e8f0);
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        .stat-number {
          display: block;
          font-size: 2.5rem;
          font-weight: bold;
          color: #1f2937;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .category-scores {
          background: linear-gradient(135deg, #f8fafc, #e2e8f0);
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        .category-score-item {
          margin-bottom: 16px;
        }

        .category-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .category-name {
          font-weight: 600;
          color: #1f2937;
        }

        .category-score {
          font-weight: bold;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .category-score.excellent {
          background: #d1fae5;
          color: #065f46;
        }
        .category-score.good {
          background: #dbeafe;
          color: #1e40af;
        }
        .category-score.warning {
          background: #fef3c7;
          color: #92400e;
        }
        .category-score.poor {
          background: #fee2e2;
          color: #991b1b;
        }

        .score-bar {
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .score-fill {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #10b981);
          transition: width 0.8s ease;
        }

        .recommendations-card,
        .critical-issues-card {
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        .recommendations-card {
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border-color: #bbf7d0;
        }

        .critical-issues-card {
          background: linear-gradient(135deg, #fef2f2, #fecaca);
          border-color: #fca5a5;
        }

        .recommendations-card h3,
        .critical-issues-card h3 {
          margin-bottom: 16px;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .recommendations-card ul,
        .critical-issues-card ul {
          list-style: none;
          padding: 0;
        }

        .recommendations-card li,
        .critical-issues-card li {
          padding: 8px 0;
          color: #374151;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          font-size: 0.9rem;
        }

        .analysis-section {
          padding: 40px;
        }

        .analysis-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .analysis-header h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .refresh-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .refresh-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .refresh-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .analysis-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px;
        }

        .category-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          background: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          border-bottom: 1px solid #e5e7eb;
        }

        .category-header h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1f2937;
        }

        .score-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: bold;
        }

        .score-badge.excellent {
          background: #d1fae5;
          color: #065f46;
        }
        .score-badge.good {
          background: #dbeafe;
          color: #1e40af;
        }
        .score-badge.warning {
          background: #fef3c7;
          color: #92400e;
        }
        .score-badge.poor {
          background: #fee2e2;
          color: #991b1b;
        }

        .category-items {
          padding: 24px;
        }

        .analysis-item {
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f3f4f6;
        }

        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .item-name {
          font-weight: 500;
          color: #374151;
        }

        .item-status {
          font-size: 0.9rem;
        }

        .item-status.pass {
          color: #10b981;
        }
        .item-status.warning {
          color: #f59e0b;
        }
        .item-status.fail {
          color: #ef4444;
        }

        .item-message {
          font-size: 0.85rem;
          color: #6b7280;
          margin-top: 4px;
        }

        .category-issues {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .category-issues h5 {
          font-size: 0.9rem;
          font-weight: 600;
          color: #ef4444;
          margin-bottom: 8px;
        }

        .category-issues ul {
          list-style: none;
          padding: 0;
        }

        .category-issues li {
          font-size: 0.85rem;
          color: #6b7280;
          margin-bottom: 4px;
          padding-left: 12px;
          position: relative;
        }

        .category-issues li::before {
          content: '•';
          color: #ef4444;
          font-weight: bold;
          position: absolute;
          left: 0;
        }

        .tools-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
          padding: 40px;
        }

        .tool-card {
          background: white;
          padding: 28px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .tool-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .tool-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .tool-header h3 {
          font-size: 1.2rem;
          font-weight: 600;
          color: #1f2937;
        }

        .tool-header i {
          font-size: 1.5rem;
          color: #3b82f6;
        }

        .tool-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin: 24px 0;
        }

        .tool-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 20px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .tool-button.primary {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
        }

        .tool-button.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .tool-button.secondary {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .tool-button.secondary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .validation-links,
        .competitor-tools {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin: 24px 0;
        }

        .validation-link {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #3b82f6;
          text-decoration: none;
          padding: 12px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.2s ease;
          font-weight: 500;
        }

        .validation-link:hover {
          background: #f8fafc;
          border-color: #3b82f6;
          color: #2563eb;
        }

        .tool-card p {
          color: #6b7280;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .overview-grid {
            grid-template-columns: 1fr;
            gap: 20px;
            padding: 20px;
          }

          .tab-navigation {
            flex-direction: column;
          }

          .analysis-grid,
          .tools-grid {
            grid-template-columns: 1fr;
            padding: 20px;
          }

          .dashboard-stats {
            flex-direction: column;
            gap: 12px;
          }

          .quick-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};